import {
  Component,
  ViewEncapsulation,
  ViewChild,
  OnInit,
  QueryList,
  ViewChildren,
  HostListener,
} from "@angular/core";
import { environment } from "../../../environments/environment";
import { ActivatedRoute } from "@angular/router";
import { Questionnaire } from "../../interfaces/questionnaire";
import { QuestionnaireService } from "../../services/questionnaire/questionnaire.service";
import { DeleteItemConfirmationDialogComponent } from "../delete-item-confirmation-dialog/delete-item-confirmation-dialog.component";
import { Observable, Subject } from "rxjs";
import { QuestionnairePasswordDialogComponent } from "../questionnaire-password-dialog/questionnaire-password-dialog.component";
import { HttpClient } from "@angular/common/http";
import { SessionService } from "../../services/session/session.service";
import { QuestionnaireQuestionsComponent } from "../questionnaire-questions/questionnaire-questions.component";
import { MzToastService } from "ng2-materialize";
import { AddStakeholderDialogComponent } from "../add-stakeholder-dialog/add-stakeholder-dialog.component";

export interface QuestionnairePage {
  isDirty(): boolean;
  confirmDeactivation(): Promise<boolean>;
}

@Component({
  selector: "app-questionnaire",
  templateUrl: "./questionnaire.component.html",
  styleUrls: ["./questionnaire.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class QuestionnaireComponent implements OnInit, QuestionnairePage {
  env: any = environment;
  reload: Subject<void> = new Subject<void>();

  questionnaire$: Observable<Questionnaire>;
  loading$: Observable<boolean>;
  readonly$: Observable<boolean>;

  @ViewChild("FinishConfirmationModal")
  FinishConfirmationModal: DeleteItemConfirmationDialogComponent;
  @ViewChild("PasswordModal")
  PasswordModal: QuestionnairePasswordDialogComponent;
  @ViewChild("ConfirmLeave")
  ConfirmLeave: DeleteItemConfirmationDialogComponent;
  @ViewChild("DeleteItemConfirmationModal")
  DeleteItemConfirmationModal: DeleteItemConfirmationDialogComponent;
  @ViewChild("AddStakeholderModal")
  AddStakeholderModal: AddStakeholderDialogComponent;
  @ViewChildren(QuestionnaireQuestionsComponent)
  private _questions: QueryList<QuestionnaireQuestionsComponent>;

  constructor(
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private questionnaireService: QuestionnaireService,
    private http: HttpClient,
    private toastService: MzToastService
  ) {}

  ngOnInit(): void {
    this.questionnaire$ = this.route.params
      .merge(
        this.reload
          .withLatestFrom(this.route.params)
          .map(([_, params]) => params)
      )
      .switchMap((params: { questionnaireId: string }) =>
        this.questionnaireService
          .getQuestionnaireByShortId(params.questionnaireId)
          .catch((error) =>
            this.PasswordModal.getResult().switchMap((password) =>
              this.http
                .post<string>(
                  this.env.managirUrl + "/actions/login/questionnaire",
                  {
                    password: password,
                    questionnaireId: params.questionnaireId,
                  },
                  {
                    headers: { "Content-Type": "application/json" },
                  }
                )
                .map((token) => {
                  this.sessionService.setItem("questionnaire-token", token);
                  throw new Error();
                })
            )
          )
          .retry()
      )
      .shareReplay(1);

    this.loading$ = this.route.params
      .map((_) => true)
      .merge(this.reload.map((_) => true))
      .merge(this.questionnaire$.map((_) => false));
    this.readonly$ = this.questionnaire$
      .combineLatest(this.route.data)
      .map(
        ([questionnaire, data]) =>
          questionnaire.finished || data.readonly || false
      );
  }

  async finishQuestionnaire() {
    let result = await this.FinishConfirmationModal.getResultAsync();
    if (result === true) {
      let questionnaire = await this.questionnaire$.first().toPromise();
      questionnaire.finished = true;
      await this.saveAndMarkPristine(questionnaire);
      this.reload.next();
    }
  }

  async save() {
    let questionnaire = await this.questionnaire$.first().toPromise();
    await this.saveAndMarkPristine(questionnaire);
    this.toastService.show(
      "Fragebogen wurde erfolgreich gespeichert.",
      4000,
      "confirm-toast"
    );
  }

  async saveAndMarkPristine(questionnaire: Questionnaire) {
    await this.questionnaireService.saveQuestionnaire(questionnaire);
    this._questions.forEach((x) =>
      Object.keys(x.Form.controls).forEach((control) => {
        x.Form.controls[control].markAsPristine();
      })
    );
  }

  isDirty(): boolean {
    return this._questions
      .map((x) => x.Form.dirty)
      .reduce((x, y) => x || y, false);
  }

  confirmDeactivation(): Promise<boolean> {
    return this.ConfirmLeave.getResultAsync();
  }

  async deleteStakeholder(stakeholder) {
    let result = await this.DeleteItemConfirmationModal.getResultAsync();
    if (result === true) {
      let questionnaire = await this.questionnaire$.first().toPromise();
      questionnaire.stakeholders.splice(
        questionnaire.stakeholders.indexOf(stakeholder),
        1
      );
      await this.saveAndMarkPristine(questionnaire);
    }
  }

  async openNewStakeholderDialog() {
    this.AddStakeholderModal.modalComponent.open();
  }

  async createStakeholderClicked(newStakeholder) {
    let questionnaire = await this.questionnaire$.first().toPromise();
    if (
      questionnaire.stakeholders.filter((x) => x.name === newStakeholder.name)
        .length === 0
    ) {
      questionnaire.stakeholders.push(newStakeholder);
      await this.saveAndMarkPristine(questionnaire);
      this.reload.next();
    }
  }

  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event) {
    if (this.isDirty()) {
      event.returnValue = "Leave site? Changes you made will not be saved.";
      return false;
    }
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/questionnaire/questionnaire.component.ts